import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useDeviceStore } from '@/stores/device.store';
import { useLogStore } from '@/stores/log.store';
import { useAppStore } from '@/stores/app.store';
import { webAdbService } from '@/services/webadb.service';

interface HiTrackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function HiTrackDialog({ open, onOpenChange }: HiTrackDialogProps) {
  const { adb, isConnected, deviceInfo } = useDeviceStore();
  const { addLog } = useLogStore();
  const { setStatus, setProgress } = useAppStore();

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isExtracting, setIsExtracting] = useState(false);

  const handleExtract = async () => {
    if (!adb || !isConnected) {
      addLog('error', '请先连接设备');
      return;
    }

    if (!deviceInfo?.isRooted) {
      addLog('error', '需要 ROOT 权限才能访问华为 HiTrack 运动健康数据');
      return;
    }

    try {
      // 选择保存目录
      const targetHandle = await window.showDirectoryPicker({ mode: 'readwrite' });

      setIsExtracting(true);
      setStatus('正在提取 HiTrack 数据...');

      const start = startDate ? new Date(startDate) : undefined;
      const end = endDate ? new Date(endDate + 'T23:59:59') : undefined;

      const result = await webAdbService.extractHiTrack(
        adb,
        targetHandle,
        start,
        end,
        (current, total) => {
          setProgress((current / total) * 100);
          setStatus(`正在提取: ${current}/${total}`);
        }
      );

      addLog('success', `HiTrack 提取完成: ${result.count} 个文件, ${(result.totalBytes / 1024).toFixed(1)} KB`);
      setStatus('提取完成');
      onOpenChange(false);
    } catch (e: any) {
      addLog('error', `提取失败: ${e.message}`);
      setStatus('提取失败');
    } finally {
      setIsExtracting(false);
      setProgress(null);
    }
  };

  const setQuickDate = (days: number) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    setStartDate(start.toISOString().slice(0, 10));
    setEndDate(end.toISOString().slice(0, 10));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>华为 HiTrack 运动数据提取</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setQuickDate(0)}>今天</Button>
            <Button variant="outline" size="sm" onClick={() => setQuickDate(7)}>最近7天</Button>
            <Button variant="outline" size="sm" onClick={() => setQuickDate(30)}>最近30天</Button>
            <Button variant="outline" size="sm" onClick={() => { setStartDate(''); setEndDate(''); }}>全部</Button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>开始日期</Label>
              <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>结束日期</Label>
              <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
            </div>
          </div>

          {!isConnected && <p className="text-sm text-destructive">请先连接设备</p>}
          {isConnected && !deviceInfo?.isRooted && <p className="text-sm text-destructive">需要 ROOT 权限</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>取消</Button>
          <Button onClick={handleExtract} disabled={!isConnected || !deviceInfo?.isRooted || isExtracting}>
            {isExtracting ? '提取中...' : '选择目录并提取'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
